import { TrendingUp, TrendingDown, Clock, ArrowDownLeft, BarChart3 } from "lucide-react";
import NumberFlow from "@number-flow/react";
import { cn } from "@/utils/Functions";
import { Card, CardPanel } from "@/components/ui/card";

type Props = {
  balance: number;
  pendingIn: number;
  pendingOut: number;
  pipelineValue: number;
  periodChange: number;
  pendingInCount: number;
  pendingOutCount: number;
};

export function BalanceOverview({ balance, pendingIn, pendingOut, pipelineValue, periodChange, pendingInCount, pendingOutCount }: Props) {
  const currency: Intl.NumberFormatOptions = { style: "currency", currency: "USD", maximumFractionDigits: 0 };

  const cards = [
    {
      label: "Current Balance",
      value: balance,
      sub: (
        <div className="flex items-center gap-1">
          {periodChange >= 0 ? (
            <TrendingUp className="size-3 text-emerald-500" />
          ) : (
            <TrendingDown className="size-3 text-orange-500" />
          )}
          <span className={cn("text-xs", periodChange >= 0 ? "text-emerald-500" : "text-orange-500")}>
            {periodChange >= 0 ? "+" : ""}{periodChange}% from last month
          </span>
        </div>
      ),
      icon: TrendingUp,
      accent: "",
    },
    {
      label: "Pending Inflows",
      value: pendingIn,
      sub: <span className="text-xs text-muted-foreground">{pendingInCount} pending transaction{pendingInCount !== 1 ? "s" : ""}</span>,
      icon: ArrowDownLeft,
      accent: "border-amber-500/20",
    },
    {
      label: "Pending Outflows",
      value: pendingOut,
      sub: <span className="text-xs text-muted-foreground">{pendingOutCount} scheduled/pending</span>,
      icon: Clock,
      accent: "border-orange-500/20",
    },
    {
      label: "Deal Pipeline",
      value: pipelineValue,
      sub: <span className="text-xs text-muted-foreground">Active deal value</span>,
      icon: BarChart3,
      accent: "",
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
      {cards.map((card) => (
        <Card key={card.label} className={cn("gap-2", card.accent)}>
          <CardPanel className="p-4 py-3 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">{card.label}</span>
              <card.icon className="size-4 text-muted-foreground" />
            </div>
            <NumberFlow value={card.value} format={currency} className="text-3xl font-semibold text-foreground tabular-nums" />
            {card.sub}
          </CardPanel>
        </Card>
      ))}
    </div>
  );
}
